import React, { useEffect, useRef, useState } from "react";

import type { ClientId } from "../../domain/types";
import { useCoClient } from "../../data/store";
import {
  createDirectTaxRate,
  createLedgerAccount,
  fetchTaxSections,
  setDirectTaxRateStatus,
  updateDirectTaxRate,
  type NewDirectTaxRate,
  type TaxSectionDTO,
  type TaxSectionsByVersion,
} from "../../data/taxesApi";
import { fetchZohoRecords, type ZohoLedgerDTO, type ZohoTaxDTO } from "../../data/zohoRecordsApi";
import {
  RATE_STATE_LABEL,
  TAX_LEDGERS,
  defaultLedgerFor,
  groupByCover,
  ledgerChoices,
  ledgerSlots,
  ratePercent,
  rateState,
  sectionLabel,
  todayIso,
  type LedgerSide,
  type LedgerSlot,
  type RateGroups,
  type RateState,
  type TaxKind,
} from "../../domain/taxes";
import { Callout } from "../../components/Callout";
import { SectionPicker } from "../../components/SectionPicker";
import { CoButton } from "../../components/Button";
import { EllipsisIcon } from "../../components/icons";
import { SubStepShell } from "../../components/SubStepShell";
import { cn } from "../../components/cn";
import { useSubStepNav } from "../useSubStepNav";

/** Ledger id → name, so a rate can name its ledgers without a second read. */
type LedgerNames = Map<string, string>;

function ledgerName(names: LedgerNames, id: string | null): string | null {
  if (id === null || id === "") return null;
  return names.get(id) ?? null;
}

/** Section code → what Zoho calls it. Built from both Acts; the codes are disjoint. */
type SectionIndex = Map<string, TaxSectionDTO>;

function indexSections(byVersion: TaxSectionsByVersion): SectionIndex {
  const index: SectionIndex = new Map();
  for (const list of Object.values(byVersion)) {
    for (const section of list) index.set(section.value, section);
  }
  return index;
}

/**
 * Section and Nature of payment carry the longest strings on the row and were
 * the two getting squeezed — both wrapped to two lines on a table only 638px
 * wide, which is all the step column gives.
 *
 * The room came off Rate and Status, which hold "10%" and "Expired" and had
 * more than either needs. What is left is split 2:1 between the nature of
 * payment and the payable ledger rather than evenly: the nature is the column a
 * CA reads down, and the ledger beside it is "TDS Payable" in almost every row.
 * Its floor stops it collapsing on the rare long account name.
 *
 * Section stays fixed because its content is not uniform — the old Act codes
 * are short ("194H") and the Income Tax Act 2025 renamed them to descriptive
 * keys, so "Payment to non union employee" is also a section.
 */
const COLUMNS = "sm:grid-cols-[10.5rem_minmax(0,2fr)_3.5rem_minmax(5.5rem,1fr)_4.5rem]";

/**
 * A section code short and code-like enough to be worth setting in mono.
 *
 * "194H" is an identifier and reads as one. "Commission or brokerage" is a
 * sentence, and mono costs it about 15% more width for nothing — which on this
 * table is the difference between one line and two.
 */
function isCodeLike(label: string): boolean {
  return /^[\d(]/.test(label);
}

/** Zoho paints the word and nothing else; inactive gets its own colour here. */
const STATE_CLASS: Record<RateState, string> = {
  active: "text-muted-foreground",
  inactive: "text-amber-700 dark:text-amber-400",
  expired: "text-red-600 dark:text-red-400",
};

/** One line of a row menu. A disabled item stays listed rather than vanishing. */
interface MenuItem {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
}

/**
 * The actions Zoho itself offers on a rate, behind the same "⋯" it uses.
 *
 * A trigger rather than two buttons because the table has 638px for five packed
 * columns (see COLUMNS), and a pair of visible buttons on every row would take
 * that width off the two columns carrying words. It costs nothing until pressed:
 * absolutely positioned at the row's right edge, in the slack the status column
 * leaves after "Inactive".
 *
 * Closes on Escape and on a press anywhere outside it, both listened for on the
 * document — the menu is one row of many, and a press on a sibling row is
 * "close this", not "do nothing".
 */
function RowMenu({ items, busy }: { items: MenuItem[]; busy: boolean }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent) {
      if (boxRef.current !== null && !boxRef.current.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={boxRef} className="absolute right-0 top-1.5">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        disabled={busy}
        aria-label="Rate actions"
        aria-expanded={open}
        className={cn(
          "flex size-5 items-center justify-center rounded text-muted-foreground outline-none hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50",
          open && "bg-muted text-foreground"
        )}
      >
        <EllipsisIcon className={cn("size-4", busy && "animate-pulse")} />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-20 mt-1 w-44 rounded-md border bg-background p-1 shadow-md">
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              disabled={item.disabled}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
              className="block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function RateRow({
  rate,
  names,
  kind,
  state,
  sections,
  actions,
}: {
  rate: ZohoTaxDTO;
  names: LedgerNames;
  kind: TaxKind;
  state: RateState;
  sections: SectionIndex;
  /** The row's own menu, which Zoho offers on both taxes' lists. */
  actions: React.ReactNode;
}) {
  // The payable side, which is the one that matters in a list: it is what the
  // client will owe the government. WHICH FIELD HOLDS IT DEPENDS ON THE TAX —
  // `tds_payable_account_id` for TDS, `tax_account_id` for TCS — so it is read
  // through the table that decides that rather than guessed here.
  //
  // `output_tax_account_name` is the fallback because Zoho fills it on the list
  // read, before the detail call that resolves the id has happened.
  const payable =
    ledgerName(names, rate[TAX_LEDGERS[kind].payable.field] ?? null) ?? rate.output_tax_account_name;

  // Zoho's own rates list puts `section_no` in this column — "Section 393(1)
  // Sl1(ii)", not the raw key — so this reads the same as the screen a CA will
  // check it against. The humanised code is the fallback for a section the
  // list did not cover.
  const known = rate.tax_specific_type === null ? undefined : sections.get(rate.tax_specific_type);
  const section = known?.section_no || sectionLabel(rate.tax_specific_type);

  return (
    <li
      className={cn(
        "relative grid items-baseline gap-x-4 gap-y-1 border-b py-2 last:border-b-0",
        COLUMNS
      )}
    >
      <span className="flex flex-col">
        <span className={cn("text-sm", isCodeLike(section) && "font-mono")}>{section}</span>
        {/* The Act 1961 number the 2025 section replaced. A CA thinks in these. */}
        {known?.old_section_no ? (
          <span className="font-mono text-[11px] text-muted-foreground">was {known.old_section_no}</span>
        ) : null}
      </span>
      <span className="text-sm">{rate.tax_name}</span>
      <span className="text-sm sm:text-right">{ratePercent(rate.tax_percentage)}%</span>
      <span className={cn("text-sm", payable === null && "italic text-muted-foreground")}>
        {payable ?? "not read"}
      </span>
      <span className={cn("text-sm", STATE_CLASS[state])}>{RATE_STATE_LABEL[state]}</span>
      {actions}
    </li>
  );
}

/**
 * One tax's rates, as its own panel.
 *
 * TDS and TCS are two separate taxes under two separate chapters, deducted by
 * different parties on different transactions, and a CA works one at a time. So
 * each gets a bordered panel with its own heading, its own count and its own
 * add action, rather than the two running together down one page separated by a
 * rule — which read as one long list with a subheading in the middle.
 *
 * Inside, the shape is Zoho's own Income TDS Rates screen: a flat table with the
 * status in the last column, rather than expired rates hidden behind a
 * disclosure. They are not a footnote — a back-dated bill is given the rate
 * that applied at the time, so a CA reading this needs to see them.
 *
 * Usable ones sort first, which is the only thing Zoho's list does not do and
 * the one place it is worth differing: with fifteen of sixteen expired, the
 * one that still works should not be somewhere in the middle.
 */
function RateSection({
  title,
  kind,
  intro,
  groups,
  names,
  sections,
  today,
  emptyNote,
  freshId,
  action,
  rowMenu,
}: {
  title: string;
  intro: string;
  kind: TaxKind;
  groups: RateGroups;
  names: LedgerNames;
  sections: SectionIndex;
  today: string;
  emptyNote: string;
  freshId: string | null;
  action: React.ReactNode;
  rowMenu: (rate: ZohoTaxDTO, state: RateState) => React.ReactNode;
}) {
  const total = groups.active.length + groups.historic.length;
  const inactive = groups.historic.filter((rate) => rateState(rate, today) === "inactive").length;
  const expired = groups.historic.length - inactive;

  const row = (rate: ZohoTaxDTO) => {
    const state = rateState(rate, today);

    return (
      <div
        key={rate.tax_id}
        className={cn("rounded-md", rate.tax_id === freshId && "bg-primary/5 ring-1 ring-primary/30")}
      >
        <RateRow
          rate={rate}
          names={names}
          kind={kind}
          state={state}
          sections={sections}
          actions={rowMenu(rate, state)}
        />
      </div>
    );
  };

  return (
    <section className="flex flex-col gap-3 rounded-lg border p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-base font-medium">{title}</h3>
        {total > 0 && (
          <span className="text-xs text-muted-foreground">
            {groups.active.length} active
            {expired > 0 && ` · ${expired} expired`}
            {inactive > 0 && ` · ${inactive} inactive`}
          </span>
        )}
      </div>

      <p className="text-sm text-muted-foreground">{intro}</p>

      {total === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyNote}</p>
      ) : (
        <>
          <div
            className={cn(
              "hidden gap-x-4 border-b pb-1 text-xs font-medium text-muted-foreground sm:grid",
              COLUMNS
            )}
          >
            <span>Section</span>
            <span>Nature of payment</span>
            <span className="text-right">Rate</span>
            <span>Payable ledger</span>
            <span>Status</span>
          </div>
          <ul className="flex flex-col">
            {groups.active.map(row)}
            {groups.historic.map(row)}
          </ul>
        </>
      )}

      {/*
        One per panel rather than one for the page: the two taxes take different
        section vocabularies, so "add a rate" is a different form depending on
        which list you are looking at, and a shared button would have to ask
        which one first.
      */}
      <div className="border-t pt-3">{action}</div>
    </section>
  );
}

const INPUT_CLASS = "h-9 w-full rounded-md border bg-background px-2 text-sm";

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">
        {label}
        {hint && <span className="font-normal text-muted-foreground/70"> · {hint}</span>}
      </span>
      {children}
    </label>
  );
}

/**
 * The financial year this date falls in, as its 1 April.
 *
 * Zoho's own dialog opens with the current FY start already filled, and a rate
 * that begins part-way through a year is the exception rather than the rule.
 */
function financialYearStart(today: Date): string {
  const year = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  return `${year}-04-01`;
}

/**
 * The longest tax name Zoho will take.
 *
 * Its refusal reads 'Please ensure that the "Tax Name" has less than 50
 * characters', which is off by one: 50 is accepted and 51 is refused, both
 * proven by renaming a rate. Trust the behaviour, not the sentence.
 *
 * Mirrored by `TdsRateCreator::NAME_MAX`, which refuses the same length. This
 * one only stops the typing; that one is the guard.
 */
const TAX_NAME_MAX = 50;

/**
 * A section's description, cut down to something Zoho will accept as a name.
 *
 * NOT A NICETY. Twenty-nine of the sixty-seven Act 2025 descriptions are longer
 * than the limit and the longest runs to 110 characters, so seeding the name
 * from the description unshortened produced a name Zoho refused on nearly half
 * the sections — and only at the moment somebody pressed the button.
 *
 * Cut back to the last whole word rather than mid-syllable, unless that would
 * throw away most of the line, in which case a hard cut says more than three
 * words would. Either way it lands in an editable field: this is a starting
 * point, not a decision.
 */
function shortTaxName(description: string): string {
  const text = description.trim();
  if (text.length <= TAX_NAME_MAX) return text;

  const cut = text.slice(0, TAX_NAME_MAX);
  const lastSpace = cut.lastIndexOf(" ");

  return (lastSpace > TAX_NAME_MAX * 0.6 ? cut.slice(0, lastSpace) : cut).trim();
}

const EMPTY_FORM = {
  tax_name: "",
  tax_percentage: "",
  tax_specific_type: "",
  version: 2 as 1 | 2,
  start_date: "",
  end_date: "",
  /** Keyed by Zoho's payload field, which differs per tax. Empty means "let Zoho pick". */
  ledgers: {} as Partial<Record<string, string>>,
};

type RateDraft = typeof EMPTY_FORM;

/**
 * The ledger ids a payload can carry.
 *
 * Its own type so the form can fill them in one loop keyed by `LedgerSlot` and
 * then send them to either call: create and update take the same three optional
 * fields, of which any one tax uses two.
 */
type LedgerFields = Pick<NewDirectTaxRate, LedgerSlot>;

/**
 * Which Act a section belongs to, worked out from the section itself.
 *
 * A rate carries its section code and not the Act it was written under, so on an
 * edit the Act has to be recovered. The two vocabularies are disjoint — the 2025
 * Act renamed every section — so finding the code in one list is the answer.
 *
 * Falls back to the 2025 Act, which is what the add form opens on, for a code
 * neither list covers. Zoho will refuse it on submit either way, and saying so
 * there is better than the form quietly choosing the other Act.
 */
function versionFor(code: string | null, byVersion: TaxSectionsByVersion | null): 1 | 2 {
  if (code === null || code === "" || byVersion === null) return 2;

  for (const version of [1, 2] as const) {
    if ((byVersion[String(version)] ?? []).some((section) => section.value === code)) return version;
  }

  return 2;
}

/** A rate as the form holds it, for editing one that already exists in Zoho. */
function draftFrom(
  rate: ZohoTaxDTO,
  kind: TaxKind,
  sections: TaxSectionsByVersion | null
): RateDraft {
  const sides = TAX_LEDGERS[kind];
  const ledgers: Partial<Record<string, string>> = {};

  // Both sides come across as they are. An edit that leaves them out would not
  // be leaving them alone: Zoho's update is a full replace, so a slot sent empty
  // is a slot Zoho re-decides.
  for (const side of [sides.payable, sides.receivable]) {
    const account = rate[side.field];
    if (account !== null && account !== undefined && account !== "") {
      ledgers[side.field] = account;
    }
  }

  return {
    tax_name: rate.tax_name ?? "",
    // "2", not "2.0000" — the field is typed in, and Zoho takes either.
    tax_percentage: ratePercent(rate.tax_percentage),
    tax_specific_type: rate.tax_specific_type ?? "",
    version: versionFor(rate.tax_specific_type, sections),
    start_date: rate.start_date ?? "",
    end_date: rate.end_date ?? "",
    ledgers,
  };
}

/**
 * The option value that means "make a new account" rather than naming one.
 *
 * Never sent anywhere and never stored on the form: picking it opens the
 * creator and leaves the select on whatever it said before, so cancelling
 * costs nothing. Prefixed past any chance of colliding with a Zoho account id,
 * which are long digit strings.
 */
const NEW_ACCOUNT = "__new_account__";

/**
 * One of the two ledger slots, with the account Zoho would pick named rather
 * than described.
 *
 * "Zoho's default" on its own asks a CA to trust a black box on the one screen
 * where they are checking somebody else's books. Naming it — "TDS Payable" —
 * costs a line and answers the question before it is asked.
 *
 * When nothing can be inferred, Zoho's own name for the account goes in as the
 * hint rather than the vague wording. On TCS that is the usual case and it is
 * not ignorance: the account does not exist yet, and Zoho will create it when
 * the first rate is saved.
 */
function LedgerSlotField({
  label,
  side,
  fallback,
  choices,
  value,
  selected,
  onChange,
  onNew,
}: {
  label: string;
  side: LedgerSide;
  fallback: ZohoLedgerDTO | undefined;
  choices: ZohoLedgerDTO[];
  value: string;
  /** The account `value` names, when it is one the choices leave out. */
  selected: ZohoLedgerDTO | undefined;
  onChange: (value: string) => void;
  /** Open the account creator for this slot — the "+ New account…" option. */
  onNew: () => void;
}) {
  const zohoPicks = fallback?.account_name ?? side.zohoDefaultName;

  // An edited rate can name an account the choices exclude — a ledger somebody
  // has since deactivated in Zoho. Listing it keeps the select honest: without
  // it the field would read as "Zoho's default" and saving would move the rate
  // off the account it is actually on.
  const offList =
    value !== "" && selected !== undefined && !choices.some((one) => one.account_id === value)
      ? selected
      : undefined;

  return (
    <Field label={label} hint={side.accountType.replace(/_/g, " ")}>
      <select
        value={value}
        onChange={(event) =>
          // "+ New account…" is a verb in a list of nouns: it opens the
          // creator and the select stays where it was, so backing out of the
          // idea does not first move the rate somewhere.
          event.target.value === NEW_ACCOUNT ? onNew() : onChange(event.target.value)
        }
        className={INPUT_CLASS}
      >
        <option value="">{zohoPicks} - Zoho's default</option>
        {choices
          // The account Zoho would have picked is already named on the blank
          // option, so it is not repeated below it — unless it is the one this
          // rate actually names, which an edit does say out loud. Dropping it
          // then would leave the select with no option matching its value.
          .filter((ledger) => ledger.account_id !== fallback?.account_id || ledger.account_id === value)
          .map((ledger) => (
            <option key={ledger.account_id} value={ledger.account_id}>
              {ledger.account_name ?? ledger.account_id}
            </option>
          ))}
        {offList !== undefined && (
          <option value={offList.account_id}>{offList.account_name ?? offList.account_id}</option>
        )}
        {/* Last, where Zoho's own dialog keeps it. The answer to "the account
            I want is not in this list" lives in the list that raised it. */}
        <option value={NEW_ACCOUNT}>+ New account…</option>
      </select>
    </Field>
  );
}

/**
 * Make one account in the client's chart, from inside the rate form.
 *
 * INLINE RATHER THAN A DIALOG, matching how this form already reveals things —
 * the ledger fields themselves sit behind a link. One field, because
 * everything else about the account is already decided by the slot that opened
 * this: the type is fixed (payable → other current liability, receivable →
 * other current asset), which is also what makes the created account one the
 * dropdown will actually offer and Zoho will actually accept on the rate.
 *
 * THIS WRITES TO THE CLIENT'S BOOKS on its own button, before the rate is
 * saved — the account has to exist for the rate to point at it. Gentler than
 * the rate write: Zoho keeps account names unique, so a double-press refuses
 * rather than twins. The button says where it is going, same as the rate's.
 */
function NewAccountForm({
  label,
  side,
  busy,
  error,
  onAdd,
  onCancel,
}: {
  label: string;
  side: LedgerSide;
  busy: boolean;
  error: string | null;
  onAdd: (name: string) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState("");
  const ready = name.trim() !== "" && !busy;

  return (
    <div className="flex flex-col gap-2 rounded-md border border-dashed p-3">
      <Field label={`New account for ${label}`} hint={side.accountType.replace(/_/g, " ")}>
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          // The name is the whole form, so typing should be able to finish
          // the job — but only submit ours: without the guard this Enter would
          // reach the page's form handling instead.
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              if (ready) onAdd(name.trim());
            }
          }}
          placeholder={`e.g. ${side.zohoDefaultName} - Contractors`}
          autoFocus
          className={INPUT_CLASS}
        />
      </Field>

      {error !== null && <Callout variant="warning">{error}</Callout>}

      <div className="flex flex-wrap items-center gap-2">
        <CoButton onClick={() => onAdd(name.trim())} disabled={!ready}>
          {busy ? "Adding…" : "Add to chart of accounts"}
        </CoButton>
        <CoButton variant="outline" onClick={onCancel} disabled={busy}>
          Cancel
        </CoButton>
      </div>
    </div>
  );
}

/** What each tax's form says where the words cannot be shared. */
const FORM_WORDS: Record<TaxKind, { namePlaceholder: string; ratePlaceholder: string }> = {
  tds: { namePlaceholder: "Commission or Brokerage", ratePlaceholder: "2" },
  tcs: { namePlaceholder: "Tendu leaves", ratePlaceholder: "1" },
};

/**
 * Write a TDS or TCS rate to the client's real Zoho Books — a new one, or a
 * change to one that is already there.
 *
 * THIS WRITES, and the undo is worse than the bank form's: a rate that has been
 * used on a bill cannot be deleted in Zoho at all, only ended. A duplicate is
 * not merely untidy — it puts two identical entries in the dropdown a CA picks
 * from on every future bill, and splits the tax report across both. An edit is
 * no gentler: Zoho keeps no history of what the rate said before, so a rate
 * already cited on bills reads with the new name on all of them.
 *
 * No confirmation dialog, for the same reason as the bank form: everything that
 * will be sent is on screen and editable until the button is pressed, and the
 * button names where it is going.
 *
 * ONE FORM FOR ADD AND EDIT because Zoho's edit dialog IS its add dialog with
 * the fields filled in — same fields, same order, same limits. The only
 * differences are which call it makes and that an edit sends the end date even
 * when it is empty, Zoho's update being a full replace.
 *
 * ONE COMPONENT FOR BOTH TAXES because Zoho's two dialogs are the same dialog —
 * same fields, same order, same defaults, same fifty-character limit on the
 * name. What differs is which payload field each ledger goes in, and that is
 * read from `TAX_LEDGERS` rather than decided here. Note that it inverts:
 * `tax_account_id` is the receivable on a TDS rate and the payable on a TCS one.
 *
 * THE LEDGERS ARE HIDDEN BEHIND A LINK, exactly as Zoho hides them behind Edit.
 * Left alone, Zoho maps the rate to the accounts it keeps for the purpose, and
 * there is no setting anywhere that changes which those are — the per-rate
 * override this form exposes is Zoho's only lever. On an edit they start open
 * whenever the rate already names them, because "Zoho's default" would be a
 * claim about a rate that has already answered the question.
 *
 * No higher-rate deduction. Zoho offers one on both dialogs, for the penal
 * rates under 206AA and 206AB and their TCS counterpart, but those are facts
 * about a particular deductee at transaction time rather than part of standing
 * up a client's books.
 */
function RateForm({
  clientId,
  kind,
  editing,
  sections,
  ledgers,
  rates,
  onSaved,
  onLedgers,
  onCancel,
}: {
  clientId: ClientId;
  kind: TaxKind;
  /** The rate being changed, or null to add one. */
  editing: ZohoTaxDTO | null;
  sections: TaxSectionsByVersion | null;
  ledgers: ZohoLedgerDTO[];
  /** The rates already there — evidence of which ledgers Zoho will accept. */
  rates: ZohoTaxDTO[];
  onSaved: (
    rates: ZohoTaxDTO[],
    ledgers: ZohoLedgerDTO[],
    taxId: string,
    rereadError: string | null
  ) => void;
  /**
   * The chart of accounts changed under the form — "+ New account…" wrote to
   * it. The parent owns the list both dropdowns read from, so it goes up.
   */
  onLedgers: (ledgers: ZohoLedgerDTO[], rereadError: string | null) => void;
  onCancel: () => void;
}) {
  // Read once, at mount. The parent keys this component by the rate it is
  // editing, so switching rows remounts it rather than leaving last row's
  // typing in the fields.
  const [form, setForm] = useState<RateDraft>(() =>
    editing === null ? EMPTY_FORM : draftFrom(editing, kind, sections)
  );
  const [showLedgers, setShowLedgers] = useState(() => Object.keys(form.ledgers).length > 0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // "+ New account…", one slot at a time — the field it was opened from.
  // Opening it from the other slot moves it rather than doubling it.
  const [newFor, setNewFor] = useState<string | null>(null);
  const [addingAccount, setAddingAccount] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);
  const liveRef = useRef(true);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);

  // Edit opens from a row that can be a whole table above where the form
  // renders, so the form comes to the operator. Add doesn't scroll: its
  // button is where the form appears. block "start" rather than "center"
  // because the form is taller than half a viewport.
  useEffect(() => {
    if (editing !== null) rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [editing]);

  const label = kind.toUpperCase();
  const sides = TAX_LEDGERS[kind];
  const words = FORM_WORDS[kind];

  function set(patch: Partial<RateDraft>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  function setLedger(field: string, value: string) {
    setForm((current) => ({ ...current, ledgers: { ...current.ledgers, [field]: value } }));
  }

  /**
   * Make the account in the client's chart, then stand the slot on it.
   *
   * The fresh chart goes up to the parent — both dropdowns and the rate table
   * read from it — and this slot selects the new id, so the CA lands back on a
   * form already saying what they came to say. When Zoho created the account
   * but did not return its id (seen on the ledger import), there is nothing to
   * select; the reread_error sentence sent up says what happened and the
   * creator closes rather than inviting a second copy.
   */
  async function addAccount(field: string, name: string) {
    if (addingAccount) return;
    const side = [sides.payable, sides.receivable].find((one) => one.field === field);
    if (side === undefined) return;

    setAddingAccount(true);
    setAccountError(null);

    try {
      const result = await createLedgerAccount(clientId, {
        account_name: name,
        account_type: side.accountType,
      });
      if (!liveRef.current) return;

      onLedgers(result.ledgers, result.reread_error);
      if (result.account.account_id !== "") setLedger(field, result.account.account_id);
      setNewFor(null);
    } catch (caught: unknown) {
      if (!liveRef.current) return;
      // Zoho's refusal, or our own sentence about a taken name — either is
      // more useful on screen than anything written over it.
      setAccountError(caught instanceof Error ? caught.message : "Could not add the account.");
    } finally {
      if (liveRef.current) setAddingAccount(false);
    }
  }

  const forVersion = sections?.[String(form.version)] ?? [];
  const chosen = forVersion.find((section) => section.value === form.tax_specific_type);

  function pickSection(value: string) {
    const section = forVersion.find((candidate) => candidate.value === value);
    const suggested = shortTaxName(section?.description ?? "");
    set({
      tax_specific_type: value,
      // Zoho seeds the name from the section and lets it be overwritten. Only
      // done while the name is still untouched — compared against what this
      // would have suggested last time, not against the raw description, since
      // the two differ whenever the description had to be shortened.
      tax_name:
        form.tax_name === "" || form.tax_name === shortTaxName(chosen?.description ?? "")
          ? suggested
          : form.tax_name,
    });
  }

  const ready =
    form.tax_specific_type !== "" && form.tax_name.trim() !== "" && form.tax_percentage.trim() !== "";

  async function submit() {
    if (!ready || sending) return;

    setSending(true);
    setError(null);

    const common = {
      tax_name: form.tax_name.trim(),
      tax_percentage: form.tax_percentage.trim(),
      tax_specific_type: form.tax_specific_type,
      version: form.version,
      start_date: form.start_date || financialYearStart(new Date()),
    };

    // Only this tax's own two fields, and only when chosen. An empty one is
    // left out rather than sent blank, which is what tells Zoho to pick.
    const chosenLedgers: LedgerFields = {};
    for (const side of [sides.payable, sides.receivable]) {
      const account = form.ledgers[side.field];
      if (account !== undefined && account !== "") chosenLedgers[side.field] = account;
    }

    try {
      const result =
        editing === null
          ? await createDirectTaxRate(clientId, kind, {
              ...common,
              end_date: form.end_date || undefined,
              ...chosenLedgers,
            })
          : await updateDirectTaxRate(clientId, kind, {
              ...common,
              tax_id: editing.tax_id,
              // Sent even when empty. Zoho's update replaces the rate outright,
              // so leaving this out would keep whatever end date is on it —
              // which is exactly what somebody clearing the field means to undo.
              end_date: form.end_date,
              ...chosenLedgers,
            });
      if (!liveRef.current) return;

      const savedId = "created" in result ? result.created.tax_id : result.updated.tax_id;
      onSaved(result.rates, result.ledgers, savedId, result.reread_error);
    } catch (caught: unknown) {
      if (!liveRef.current) return;
      // Zoho's own refusal comes through here and is worth more on screen than
      // anything we would write over it.
      setError(
        caught instanceof Error
          ? caught.message
          : editing === null
            ? "Could not add the rate."
            : "Could not save the rate."
      );
    } finally {
      if (liveRef.current) setSending(false);
    }
  }

  // Not simply "every account of the right type" — Zoho reserves most of them.
  // The rule, and the evidence for it, live in ledgerChoices.
  const slots = ledgerSlots(kind);
  const forPayable = ledgerChoices(ledgers, rates, slots, sides.payable.field);
  const forReceivable = ledgerChoices(ledgers, rates, slots, sides.receivable.field);
  const defaultPayable = defaultLedgerFor(rates, ledgers, sides.payable.field);
  const defaultReceivable = defaultLedgerFor(rates, ledgers, sides.receivable.field);

  const overridden = [sides.payable, sides.receivable].some(
    (side) => (form.ledgers[side.field] ?? "") !== ""
  );

  const named = (id: string | undefined): ZohoLedgerDTO | undefined =>
    id === undefined || id === "" ? undefined : ledgers.find((ledger) => ledger.account_id === id);

  return (
    <div ref={rootRef} className="flex flex-col gap-4 rounded-lg border bg-muted/20 p-4 scroll-mt-4">
      <h4 className="text-sm font-semibold">
        {editing === null ? `New ${label} rate` : `Edit ${label} rate`}
      </h4>

      {/* What the rate IS. Section leads, because everything else follows from
          it — Zoho puts the name first, but the name is derived from here. */}
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_9rem]">
        <div className="flex flex-col gap-1">
          {/* The count lives in the picker's own footer, where it is next to
              the list it counts rather than repeated on the label. */}
          <span className="text-xs font-medium text-muted-foreground">Section</span>
          <SectionPicker
            sections={forVersion}
            value={form.tax_specific_type}
            onChange={pickSection}
          />
        </div>

        <Field label="Applicable Act">
          <select
            value={String(form.version)}
            onChange={(event) => {
              // The vocabularies do not overlap, so a section chosen under one
              // Act is not a section under the other. Cleared rather than
              // carried across, where it would fail validation on submit.
              set({ version: Number(event.target.value) as 1 | 2, tax_specific_type: "" });
            }}
            className={INPUT_CLASS}
          >
            <option value="2">Act 2025</option>
            <option value="1">Act 1961</option>
          </select>
        </Field>
      </div>

      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_9rem]">
        <Field
          label="Tax name"
          hint={
            // Only once it is close enough to matter. A counter on an empty
            // field is noise; a counter at 44 of 50 is a warning.
            form.tax_name.length > TAX_NAME_MAX - 10
              ? `${form.tax_name.length}/${TAX_NAME_MAX}`
              : "what appears on the bill"
          }
        >
          <input
            value={form.tax_name}
            onChange={(event) => set({ tax_name: event.target.value })}
            placeholder={words.namePlaceholder}
            // Zoho's cap, enforced where somebody can still do something about
            // it rather than on the button press.
            maxLength={TAX_NAME_MAX}
            className={INPUT_CLASS}
          />
        </Field>

        <Field label="Rate (%)">
          <input
            value={form.tax_percentage}
            onChange={(event) => set({ tax_percentage: event.target.value })}
            inputMode="decimal"
            placeholder={words.ratePlaceholder}
            className={INPUT_CLASS}
          />
        </Field>
      </div>

      {/* When it applies. Its own group because the dates are the part a CA
          changes least and reads past most. */}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Applicable from">
          <input
            type="date"
            value={form.start_date || financialYearStart(new Date())}
            onChange={(event) => set({ start_date: event.target.value })}
            className={INPUT_CLASS}
          />
        </Field>

        <Field label="Until" hint="blank = open-ended">
          <input
            type="date"
            value={form.end_date}
            onChange={(event) => set({ end_date: event.target.value })}
            className={INPUT_CLASS}
          />
        </Field>
      </div>

      {/*
        Zoho's own default, but named. Both ledgers stay unset unless somebody
        opens this, and unset means Zoho picks — which it does the same way
        every time, there being no setting that changes it.
      */}
      <div className="rounded-md border bg-background p-3">
        {!showLedgers ? (
          <p className="text-xs text-muted-foreground">
            Tracked under{" "}
            <span className="font-medium text-foreground">
              {defaultPayable?.account_name ?? sides.payable.zohoDefaultName}
            </span>{" "}
            and{" "}
            <span className="font-medium text-foreground">
              {defaultReceivable?.account_name ?? sides.receivable.zohoDefaultName}
            </span>
            .{" "}
            {/* Not a hedge. An organisation with no rates of this kind has
                neither account in its chart yet, and Zoho makes both on the
                first one — watched happen on a live org. */}
            {defaultPayable === undefined && defaultReceivable === undefined && (
              <span>Zoho creates them if they are not there yet. </span>
            )}
            <button
              type="button"
              onClick={() => setShowLedgers(true)}
              className="font-medium text-primary underline underline-offset-2"
            >
              Use different accounts
            </button>
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-xs font-medium">Accounts</span>
              <button
                type="button"
                onClick={() => {
                  setShowLedgers(false);
                  set({ ledgers: {} });
                  // A half-made account cannot mean anything under "defaults".
                  setNewFor(null);
                  setAccountError(null);
                }}
                className="text-[11px] text-muted-foreground underline underline-offset-2"
              >
                Back to defaults
              </button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <LedgerSlotField
                label={`${label} Payable`}
                side={sides.payable}
                fallback={defaultPayable}
                choices={forPayable}
                value={form.ledgers[sides.payable.field] ?? ""}
                selected={named(form.ledgers[sides.payable.field])}
                onChange={(value) => setLedger(sides.payable.field, value)}
                onNew={() => {
                  setNewFor(sides.payable.field);
                  setAccountError(null);
                }}
              />
              <LedgerSlotField
                label={`${label} Receivable`}
                side={sides.receivable}
                fallback={defaultReceivable}
                choices={forReceivable}
                value={form.ledgers[sides.receivable.field] ?? ""}
                selected={named(form.ledgers[sides.receivable.field])}
                onChange={(value) => setLedger(sides.receivable.field, value)}
                onNew={() => {
                  setNewFor(sides.receivable.field);
                  setAccountError(null);
                }}
              />
            </div>

            {/* Under both dropdowns rather than inside one: it is wider than a
                column, and its label says which slot it lands in. Keyed by the
                slot so switching sides starts a fresh name rather than carrying
                the other side's typing across. */}
            {newFor !== null && (
              <NewAccountForm
                key={newFor}
                label={`${label} ${newFor === sides.payable.field ? "Payable" : "Receivable"}`}
                side={newFor === sides.payable.field ? sides.payable : sides.receivable}
                busy={addingAccount}
                error={accountError}
                onAdd={(name) => void addAccount(newFor, name)}
                onCancel={() => {
                  setNewFor(null);
                  setAccountError(null);
                }}
              />
            )}
          </div>
        )}
      </div>

      {error !== null && <Callout variant="warning">{error}</Callout>}

      <div className="flex flex-wrap items-center gap-2">
        {/* Held while the account creator is open: a rate saved mid-thought
            would go out pointing somewhere the CA was in the middle of moving
            it off. The sentence beside it says so, per the rule that a button
            that does nothing without saying why is worse than no button. */}
        <CoButton onClick={() => void submit()} disabled={!ready || sending || newFor !== null}>
          {editing === null
            ? sending
              ? "Adding…"
              : "Add to Zoho Books"
            : sending
              ? "Saving…"
              : "Save to Zoho Books"}
        </CoButton>
        <CoButton variant="outline" onClick={onCancel} disabled={sending || addingAccount}>
          Cancel
        </CoButton>

        {newFor !== null && (
          <span className="text-xs text-muted-foreground">
            Finish or cancel the new account first.
          </span>
        )}

        {/* One line saying exactly what is about to be written, so the last
            look before pressing the button is at the thing, not at the form. */}
        {ready && !sending && newFor === null && (
          <span className="text-xs text-muted-foreground">
            {chosen?.section_no ?? form.tax_specific_type} · {form.tax_percentage}% · from{" "}
            {form.start_date || financialYearStart(new Date())}
            {overridden ? " · custom accounts" : ""}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * The panel's own add button, and why it might not work yet.
 *
 * Disabled until the section list is in, because the form's first field is a
 * section and Zoho matches on nothing else. The reason sits beside it rather
 * than in a tooltip: a button that does nothing without saying why is worse
 * than no button.
 */
function AddRateButton({
  label,
  sections,
  sectionsError,
  onOpen,
}: {
  label: string;
  sections: TaxSectionsByVersion | null;
  sectionsError: string | null;
  onOpen: () => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <CoButton onClick={onOpen} disabled={sections === null}>
        Add More {label} Rates
      </CoButton>
      {sections === null && sectionsError === null && (
        <span className="text-xs text-muted-foreground">Reading the section list…</span>
      )}
      {sectionsError !== null && <span className="text-xs text-muted-foreground">{sectionsError}</span>}
    </div>
  );
}

/**
 * Which form a panel has open, if any.
 *
 * One at a time per panel: both forms write to the list above them and both take
 * the whole panel's width, so opening Edit puts the add form away and the other
 * way round.
 */
type OpenForm = { mode: "add" } | { mode: "edit"; rate: ZohoTaxDTO };

export function TdsTcsReviewStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const { goNext } = useSubStepNav(clientId, "tds-tcs-review");

  const [tds, setTds] = useState<ZohoTaxDTO[] | null>(null);
  const [tcs, setTcs] = useState<ZohoTaxDTO[] | null>(null);
  const [ledgers, setLedgers] = useState<ZohoLedgerDTO[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Kept per tax rather than merged. The two vocabularies are separate lists
  // from separate calls, either of which can fail on its own, and a TCS
  // failure must not blank the TDS picker.
  const [sections, setSections] = useState<Record<TaxKind, TaxSectionsByVersion | null>>({
    tds: null,
    tcs: null,
  });
  const [sectionErrors, setSectionErrors] = useState<Record<TaxKind, string | null>>({
    tds: null,
    tcs: null,
  });

  const [freshId, setFreshId] = useState<Record<TaxKind, string | null>>({ tds: null, tcs: null });
  const [rereadError, setRereadError] = useState<string | null>(null);

  const [openForm, setOpenForm] = useState<Record<TaxKind, OpenForm | null>>({
    tds: null,
    tcs: null,
  });

  // One flip at a time across both panels, and the row says which one. Two in
  // flight would have each replacing the list from a read the other had not
  // made yet.
  const [flipping, setFlipping] = useState<string | null>(null);
  // Kept per tax so the sentence lands under the panel it happened in. Both
  // lists have the menu now, and the message does not name which tax it is
  // about.
  const [flipError, setFlipError] = useState<Record<TaxKind, string | null>>({
    tds: null,
    tcs: null,
  });

  useEffect(() => {
    let live = true;
    setLoadError(null);

    // The ledgers come along to turn a rate's account id into a name. The
    // alternative was storing the name on every rate, where it would go stale
    // the first time somebody renamed the ledger in Zoho.
    fetchZohoRecords(clientId, ["tds_rates", "tcs_rates", "ledgers"])
      .then((records) => {
        if (!live) return;
        setTds(records.tds_rates ?? []);
        setTcs(records.tcs_rates ?? []);
        setLedgers(records.ledgers ?? []);
      })
      .catch((error: unknown) => {
        if (live) setLoadError(error instanceof Error ? error.message : "Could not load the TDS and TCS rates.");
      });

    return () => {
      live = false;
    };
  }, [clientId]);

  // Separate from the records read because these are the calls on this screen
  // that go out to Zoho. The table must not wait on them: without the section
  // list the rows still render, just labelled by their raw code.
  //
  // Fired together rather than one after the other. They are independent reads
  // of two different lists, and making TCS queue behind TDS would double the
  // wait before either picker works.
  useEffect(() => {
    let live = true;
    setSectionErrors({ tds: null, tcs: null });

    for (const kind of ["tds", "tcs"] as TaxKind[]) {
      fetchTaxSections(clientId, kind)
        .then((byVersion) => {
          if (live) setSections((current) => ({ ...current, [kind]: byVersion }));
        })
        .catch((error: unknown) => {
          if (!live) return;
          setSectionErrors((current) => ({
            ...current,
            [kind]:
              error instanceof Error
                ? `Could not read the section list: ${error.message}`
                : "Could not read the section list from Zoho.",
          }));
        });
    }

    return () => {
      live = false;
    };
  }, [clientId]);

  if (!client) return null;

  const clientName = client.name || "this client";
  const today = todayIso();
  const names: LedgerNames = new Map(
    ledgers.map((ledger) => [ledger.account_id, ledger.account_name ?? ledger.account_id])
  );

  const empty = new Map<string, TaxSectionDTO>();
  const sectionIndex: Record<TaxKind, SectionIndex> = {
    tds: sections.tds === null ? empty : indexSections(sections.tds),
    tcs: sections.tcs === null ? empty : indexSections(sections.tcs),
  };

  // Grouped only to sort the usable ones to the top; both groups render into
  // the same table.
  const tdsGroups = groupByCover(tds ?? [], today);
  const tcsGroups = groupByCover(tcs ?? [], today);
  const loading = tds === null || tcs === null;

  // Every write hands back one tax's list and only that one, so the other is
  // left exactly as it was rather than re-read alongside it.
  function setRates(kind: TaxKind, next: ZohoTaxDTO[]) {
    if (kind === "tds") setTds(next);
    else setTcs(next);
  }

  // Both writes hand back that tax's whole list re-read from Zoho, so there is
  // nothing else to fetch — the rate arrives with the ledgers and status Zoho
  // filled in rather than the fields we sent.
  //
  // The chart of accounts comes back too, and is not optional: Zoho invents the
  // TCS Payable and TCS Receivable accounts on an organisation's first TCS
  // rate, and without this the row would name accounts we do not hold.
  function rateSaved(
    kind: TaxKind,
    next: ZohoTaxDTO[],
    nextLedgers: ZohoLedgerDTO[],
    taxId: string,
    reread: string | null
  ) {
    setRates(kind, next);
    setLedgers(nextLedgers);
    setFreshId((current) => ({ ...current, [kind]: taxId === "" ? null : taxId }));
    setRereadError(reread);
    setOpenForm((current) => ({ ...current, [kind]: null }));
  }

  /**
   * Switch a rate on or off in Zoho, on the press.
   *
   * No confirmation, because Zoho does not ask either and the opposite menu item
   * puts it straight back. Nothing already posted with the rate is touched — the
   * switch only decides whether new transactions are offered it.
   */
  async function flipStatus(kind: TaxKind, rate: ZohoTaxDTO, active: boolean) {
    if (flipping !== null) return;

    setFlipping(rate.tax_id);
    setFlipError((current) => ({ ...current, [kind]: null }));

    try {
      const result = await setDirectTaxRateStatus(clientId, kind, rate.tax_id, active);
      setRates(kind, result.rates);
      setRereadError(result.reread_error);
    } catch (caught: unknown) {
      // Zoho's own words where there are any: it is the only thing on screen
      // that knows why the books refused.
      setFlipError((current) => ({
        ...current,
        [kind]:
          caught instanceof Error
            ? caught.message
            : `Could not ${active ? "reactivate" : "deactivate"} the rate in Zoho.`,
      }));
    } finally {
      setFlipping(null);
    }
  }

  /**
   * The row menu Zoho's own Direct Taxes screens offer, item for item.
   *
   * Edit is left off an inactive rate on purpose. Zoho offers View there and no
   * more, so a rate has to be switched back on before it can be changed — which
   * is a rule about the books, not a limitation of this screen.
   *
   * The same menu on both taxes, because it is the same menu in Zoho and the
   * calls behind it differ only in which list comes back.
   */
  function rowMenuFor(kind: TaxKind) {
    return function rowMenu(rate: ZohoTaxDTO, state: RateState) {
      // Greyed out rather than quietly ignored while another row's switch is in
      // flight, since that one is about to replace this list.
      const busy = flipping !== null;

      const items: MenuItem[] =
        state === "inactive"
          ? [
              {
                label: "Mark as active",
                disabled: busy,
                onSelect: () => void flipStatus(kind, rate, true),
              },
            ]
          : [
              {
                label: "Edit",
                // The form opens on a section picker, which is empty until the
                // section list is in — the same gate the add button has.
                disabled: sections[kind] === null,
                onSelect: () =>
                  setOpenForm((current) => ({ ...current, [kind]: { mode: "edit", rate } })),
              },
              {
                label: "Mark as inactive",
                disabled: busy,
                onSelect: () => void flipStatus(kind, rate, false),
              },
            ];

      return <RowMenu items={items} busy={flipping === rate.tax_id} />;
    };
  }

  /** The panel's add button, or whichever form it has open in that slot. */
  function panelAction(kind: TaxKind) {
    const open = openForm[kind];

    if (open === null) {
      return (
        <AddRateButton
          label={kind.toUpperCase()}
          sections={sections[kind]}
          sectionsError={sectionErrors[kind]}
          onOpen={() => setOpenForm((current) => ({ ...current, [kind]: { mode: "add" } }))}
        />
      );
    }

    return (
      <RateForm
        // Remounted per rate, so choosing Edit on a second row starts from that
        // row rather than from what was typed into the first.
        key={open.mode === "edit" ? open.rate.tax_id : "add"}
        clientId={clientId}
        kind={kind}
        editing={open.mode === "edit" ? open.rate : null}
        sections={sections[kind]}
        ledgers={ledgers}
        rates={(kind === "tds" ? tds : tcs) ?? []}
        onSaved={(next, nextLedgers, taxId, reread) =>
          rateSaved(kind, next, nextLedgers, taxId, reread)
        }
        // "+ New account…" wrote to the chart both panels read from, so the
        // list lives here. The reread sentence lands in the same callout the
        // rate writes use — it is the same "Zoho holds it, the list may lag".
        onLedgers={(nextLedgers, reread) => {
          setLedgers(nextLedgers);
          setRereadError(reread);
        }}
        onCancel={() => setOpenForm((current) => ({ ...current, [kind]: null }))}
      />
    );
  }

  // Continue is the review sign-off, so the rates have to actually be on
  // screen first — a read still in flight or one that failed is not a review.
  const reviewable = loadError === null && !loading;

  return (
    <SubStepShell
      title="TDS & TCS Settings"
      description="For each TDS and TCS rate, select the ledger where the corresponding payable amount should be posted."
      thenWhat={reviewable ? "Continue → Banks & Credit Cards." : "The rates have to load before this step can be signed off."}
      onContinue={() => void goNext()}
      continueDisabled={!reviewable}
    >
      {loadError !== null ? (
        <Callout variant="warning">{loadError}</Callout>
      ) : loading ? (
        <p className="text-sm text-muted-foreground">Reading the TDS and TCS rates…</p>
      ) : (
        <>
          <RateSection
            title="TDS rates"
            intro={`Below are the TDS rates already available in ${clientName}'s Zoho Books account. Review the rates and select the correct payable ledger for each TDS rate.`}
            kind="tds"
            groups={tdsGroups}
            names={names}
            sections={sectionIndex.tds}
            today={today}
            freshId={freshId.tds}
            emptyNote={`No TDS rates were read from ${clientName}'s books.`}
            rowMenu={rowMenuFor("tds")}
            action={panelAction("tds")}
          />

          {flipError.tds !== null && <Callout variant="warning">{flipError.tds}</Callout>}
          {rereadError !== null && <Callout variant="warning">{rereadError}</Callout>}

          <RateSection
            title="TCS rates"
            intro={`Below are the TCS rates already available in ${clientName}'s Zoho Books account. Review the rates and select the correct payable ledger for each TCS rate.`}
            kind="tcs"
            groups={tcsGroups}
            names={names}
            sections={sectionIndex.tcs}
            today={today}
            freshId={freshId.tcs}
            emptyNote={`No TCS rates were read from ${clientName}'s books.`}
            rowMenu={rowMenuFor("tcs")}
            action={panelAction("tcs")}
          />

          {flipError.tcs !== null && <Callout variant="warning">{flipError.tcs}</Callout>}
        </>
      )}
    </SubStepShell>
  );
}
